import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CsrCallService, CallStatus, SipStatus, TranscriptMsg, CaptionSegment, CallerInfo, AgentOption, WrapUpDetails, ConferenceParticipant, IncomingConferenceInfo, DirectoryExtensionItem } from '../../../core/services/csr-call.service';
import { TelephonyPluginConfigService } from '../../../core/telephony/telephony-plugin-config.service';

interface ExtensionPoolItem { extensionNumber: string; status: string; reservedByMe: boolean; reservedByUserName: string | null; }

@Component({
  selector: 'app-csr-console',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './csr-console.component.html',
  styleUrls: ['./csr-console.component.scss'],
})
export class CsrConsoleComponent implements OnInit, AfterViewChecked, OnDestroy {
  @ViewChild('chatEnd') chatEnd!: ElementRef;
  @ViewChild('captionEnd') captionEnd!: ElementRef;

  status: CallStatus = 'idle';
  sipStatus: SipStatus = 'idle';
  myExt = '';
  muted = false;
  seconds = 0;
  caller: CallerInfo = { name: 'Unknown Caller', phone: '', cnic: '', city: '', channel: 'voice' };
  transcript: TranscriptMsg[] = [];
  captions: CaptionSegment[] = [];
  captionsEnabled = false;
  captionStatus = 'off';
  recording = false;
  lastRecordingUrl = '';
  lastRecordingName = '';
  dialingTarget: { ext: string; name: string } | null = null;
  agents: AgentOption[] = [];

  extensionPool: ExtensionPoolItem[] = [];
  directoryExtensions: DirectoryExtensionItem[] = [];

  reply = '';
  wrapNote = '';
  wrapUpDetails: Omit<WrapUpDetails, 'notes'> = {
    disposition: '',
    callReason: '',
    sourceOfInformation: '',
    callbackRequired: false,
    callbackDueAt: '',
    callbackStatus: 'PENDING',
  };
  showTransfer = false;
  showConference = false;
  showDial = false;
  dialInput = '';
  dialPadValue = '';
  activeLeftTab: 'dialpad' | 'directory' = 'dialpad';
  activeRightTab: 'context' | 'transcript' | 'wrapup' = 'context';
  toast: string | null = null;
  isConference = false;
  conferenceParticipants: ConferenceParticipant[] = [];
  incomingConferenceInfo: IncomingConferenceInfo | null = null;
  isCoaching = false;
  coachingAgent = '';
  beingCoached = false;
  coachingSupervisor = '';

  private toastTimer: any = null;
  private notesDebounce: any = null;
  private readonly subs: Subscription[] = [];
  private shouldScrollChat = false;
  private shouldScrollCaptions = false;
  private autoRestored = false;
  private restoringFromServer = false;
  private get pluginConfig() {
    return this.pluginConfigService.value;
  }

  // ── Computed directory list ────────────────────────────────
  get dialableExtensions() {
    return this.directoryExtensions
      .filter(e => e.extensionNumber !== this.myExt)
      .map(e => {
        const agent = this.agents.find(a => a.ext === e.extensionNumber);
        if (e.status === 'AVAILABLE') {
          return { ext: e.extensionNumber, name: null, statusLabel: 'Not in Use', color: '#bdbdbd', canCall: false };
        }
        if (agent) {
          let statusLabel: string;
          if (agent.status === 'Available') {
            statusLabel = 'Available for Call';
          } else if (agent.status === 'On Call') {
            statusLabel = 'On Call';
          } else {
            statusLabel = 'Away';
          }
          return { ext: e.extensionNumber, name: agent.name, statusLabel, color: agent.color, canCall: agent.status === 'Available' };
        }
        return { ext: e.extensionNumber, name: e.reservedByUserName ?? `Ext ${e.extensionNumber}`, statusLabel: 'Available for Call', color: '#00c48c', canCall: true };
      });
  }

  get filteredExtensions() {
    const q = this.dialInput.trim().toLowerCase();
    if (!q) return this.dialableExtensions;
    return this.dialableExtensions.filter(e =>
      e.ext.includes(q) || (e.name ?? '').toLowerCase().includes(q)
    );
  }

  get activeCallPeerExt(): string {
    return this.dialingTarget?.ext ?? this.caller.phone ?? '';
  }

  get transferableAgents() {
    return this.agents.filter(a => a.ext !== this.activeCallPeerExt);
  }

  get onHold()    { return this.status === 'on-hold'; }
  get ended()     { return this.status === 'ended'; }
  get ringing()   { return this.status === 'ringing' && !this.dialingTarget; }
  get isIdle()    { return this.status === 'idle'; }
  get isDialing() { return this.status === 'ringing' && !!this.dialingTarget; }
  get isActive()  { return this.status === 'active' || this.onHold; }
  get showCaptionPanel() { return this.captionsEnabled || this.captionStatus !== 'off' || this.captions.length > 0; }
  get dispositionOptions() { return this.pluginConfig.wrapUpOptions.dispositions; }
  get callReasonOptions() { return this.pluginConfig.wrapUpOptions.callReasons; }
  get sourceOptions() { return this.pluginConfig.wrapUpOptions.sources; }

  get mm() { return String(Math.floor(this.seconds / 60)).padStart(2, '0'); }
  get ss() { return String(this.seconds % 60).padStart(2, '0'); }

  get sipBadge() {
    if (this.sipStatus === 'registered') return { cls: 'badge-success', dot: 'dot-ok', label: 'Registered' };
    if (this.sipStatus === 'connecting') return { cls: 'badge-warning', dot: 'dot-warn', label: 'Connecting…' };
    if (this.sipStatus === 'failed')     return { cls: 'badge-danger',  dot: 'dot-bad',  label: 'Failed' };
    return { cls: 'badge-secondary', dot: 'dot-idle', label: 'Not connected' };
  }

  get captionBadge() {
    if (this.captionStatus === 'failed')   return 'badge-danger';
    if (this.captionStatus === 'live')     return 'badge-success';
    if (this.captionStatus === 'starting') return 'badge-warning';
    return 'badge-secondary';
  }

  get agentStateLabel(): string {
    if (this.ringing) return this.incomingConferenceInfo ? 'Conference Ringing' : 'Incoming Call';
    if (this.isDialing) return 'Dialing';
    if (this.status === 'active') return 'On Call';
    if (this.onHold) return 'On Hold';
    if (this.ended) return 'Wrap-up';
    if (this.sipStatus === 'registered') return 'Ready';
    if (this.sipStatus === 'connecting') return 'Connecting';
    if (this.sipStatus === 'failed') return 'SIP Failed';
    return 'Offline';
  }

  get agentStateClass(): string {
    if (this.ringing || this.isDialing) return 'state-ringing';
    if (this.status === 'active') return 'state-active';
    if (this.onHold) return 'state-hold';
    if (this.ended) return 'state-wrapup';
    if (this.sipStatus === 'registered') return 'state-ready';
    if (this.sipStatus === 'failed') return 'state-failed';
    return 'state-idle';
  }

  get callTitle(): string {
    if (this.ringing) return this.incomingConferenceInfo ? 'Conference Call' : 'Incoming Call';
    if (this.isDialing) return 'Outbound Call';
    if (this.status === 'active') return this.isConference ? 'Conference In Progress' : 'Active Call';
    if (this.onHold) return 'Call On Hold';
    if (this.ended) return 'Call Wrap-up';
    return this.sipStatus === 'registered' ? 'Ready for Calls' : 'Not Connected';
  }

  get callSubtitle(): string {
    if (this.ringing) return 'Review caller context and answer when ready';
    if (this.isDialing) return `Ringing ${this.dialingTarget?.name || this.dialingTarget?.ext || 'number'}`;
    if (this.status === 'active') return 'Live interaction in progress';
    if (this.onHold) return 'Caller is waiting on hold';
    if (this.ended) return 'Complete disposition and notes';
    return this.sipStatus === 'registered' ? `Extension ${this.myExt} is listening` : this.sipBadge.label;
  }

  get activeCallerName(): string {
    if (this.dialingTarget) return this.dialingTarget.name || `Ext ${this.dialingTarget.ext}`;
    if (this.isIdle && !this.caller.phone) return '';
    return this.caller.name || 'Unknown Caller';
  }

  get activeCallerNumber(): string {
    if (this.dialingTarget) return this.dialingTarget.ext;
    return this.caller.phone || '';
  }

  constructor(
    public csrCall: CsrCallService,
    private readonly router: Router,
    private readonly pluginConfigService: TelephonyPluginConfigService,
  ) {}

  ngOnInit() {
    this.subs.push(
      this.csrCall.status$.subscribe(v => {
        this.status = v;
        if (v !== 'active' && v !== 'on-hold') {
          this.showTransfer = false;
          this.showConference = false;
        }
      }),
      this.csrCall.sipStatus$.subscribe(v => { this.sipStatus = v; }),
      this.csrCall.myExt$.subscribe(v => { this.myExt = v; }),
      this.csrCall.muted$.subscribe(v => { this.muted = v; }),
      this.csrCall.seconds$.subscribe(v => { this.seconds = v; }),
      this.csrCall.caller$.subscribe(v => { this.caller = v; }),
      this.csrCall.transcript$.subscribe(v => { this.transcript = v; this.shouldScrollChat = true; }),
      this.csrCall.captions$.subscribe(v => { this.captions = v; this.shouldScrollCaptions = true; }),
      this.csrCall.captionsEnabled$.subscribe(v => { this.captionsEnabled = v; }),
      this.csrCall.captionStatus$.subscribe(v => { this.captionStatus = v; }),
      this.csrCall.recording$.subscribe(v => { this.recording = v; }),
      this.csrCall.lastRecordingUrl$.subscribe(v => { this.lastRecordingUrl = v; }),
      this.csrCall.lastRecordingName$.subscribe(v => { this.lastRecordingName = v; }),
      this.csrCall.dialingTarget$.subscribe(v => { this.dialingTarget = v; }),
      this.csrCall.agents$.subscribe(v => { this.agents = v; }),
      this.csrCall.isConference$.subscribe(v => { this.isConference = v; }),
      this.csrCall.conferenceParticipants$.subscribe(v => { this.conferenceParticipants = v; }),
      this.csrCall.incomingConferenceInfo$.subscribe(v => { this.incomingConferenceInfo = v; }),
      this.csrCall.isCoaching$.subscribe(v => { this.isCoaching = v; }),
      this.csrCall.coachingAgent$.subscribe(v => { this.coachingAgent = v; }),
      this.csrCall.beingCoached$.subscribe(v => { this.beingCoached = v; }),
      this.csrCall.coachingSupervisor$.subscribe(v => { this.coachingSupervisor = v; }),
      this.csrCall.extensionPool$.subscribe(v => {
        this.extensionPool = v;
        this.tryAutoRestore(v);
      }),
      this.csrCall.directoryPool$.subscribe(v => { this.directoryExtensions = v; }),
    );
    void this.csrCall.fetchExtensionPool();
    void this.csrCall.fetchDirectoryPool();
  }

  private tryAutoRestore(pool: ExtensionPoolItem[]) {
    if (this.autoRestored || this.myExt || this.restoringFromServer) return;
    const savedExt = localStorage.getItem(this.pluginConfig.storageKeys.sipExtension)
      ?? localStorage.getItem(this.pluginConfig.storageKeys.legacySipExtension);
    const savedPass = localStorage.getItem(this.pluginConfig.storageKeys.sipPassword)
      ?? localStorage.getItem(this.pluginConfig.storageKeys.legacySipPassword);
    if (!savedExt) return;

    const stillMine = pool.some(e => e.extensionNumber === savedExt && e.reservedByMe);
    if (stillMine) {
      this.autoRestored = true;
      if (savedPass) {
        this.csrCall.selectExtension(savedExt, savedPass);
      } else {
        this.restoringFromServer = true;
        this.csrCall.reserveAndSelect(savedExt).finally(() => {
          this.restoringFromServer = false;
        });
      }
    } else {
      this.autoRestored = true;
      localStorage.removeItem(this.pluginConfig.storageKeys.sipExtension);
      localStorage.removeItem(this.pluginConfig.storageKeys.sipPassword);
      localStorage.removeItem(this.pluginConfig.storageKeys.legacySipExtension);
      localStorage.removeItem(this.pluginConfig.storageKeys.legacySipPassword);
    }
  }

  ngAfterViewChecked() {
    if (this.shouldScrollChat && this.chatEnd) {
      this.chatEnd.nativeElement.scrollIntoView({ behavior: 'smooth' });
      this.shouldScrollChat = false;
    }
    if (this.shouldScrollCaptions && this.captionEnd) {
      this.captionEnd.nativeElement.scrollIntoView({ behavior: 'smooth' });
      this.shouldScrollCaptions = false;
    }
  }

  ngOnDestroy() {
    this.subs.forEach(s => s.unsubscribe());
  }

  showToast(msg: string) {
    this.toast = msg;
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => { this.toast = null; }, 3000);
  }

  refreshDirectory() {
    void this.csrCall.fetchDirectoryPool();
    void this.csrCall.fetchAgentsPublic(this.myExt);
  }

  handleSend() {
    if (!this.reply.trim()) return;
    this.csrCall.sendMessage(this.reply);
    this.reply = '';
  }

  handleMute() {
    if (this.muted) {
      this.csrCall.unmuteCall();
      this.showToast('Microphone unmuted');
    } else {
      this.csrCall.muteCall();
      this.showToast('Microphone muted');
    }
  }

  handleHold() {
    if (this.onHold) {
      this.csrCall.resumeCall();
      this.showToast('Call resumed');
    } else {
      this.csrCall.holdCall();
      this.showToast('Call on hold');
    }
  }

  handleTransfer(ext: string, name: string) {
    this.csrCall.transferCall(ext, name);
    this.showTransfer = false;
    this.showToast(`Transferred to ${name}`);
  }

  handleConference(ext: string, name: string) {
    this.csrCall.startConference(ext, name);
    this.showConference = false;
    this.showToast(`Conference started with ${name}`);
  }

  handleDial() {
    const target = (this.dialPadValue || this.dialInput).trim();
    if (!target) return;
    this.csrCall.makeCall(target);
    this.showDial = false;
    this.dialInput = '';
    this.dialPadValue = '';
  }

  appendDialDigit(digit: string) {
    this.dialPadValue = `${this.dialPadValue}${digit}`;
    this.dialInput = this.dialPadValue;
  }

  backspaceDial() {
    this.dialPadValue = this.dialPadValue.slice(0, -1);
    this.dialInput = this.dialPadValue;
  }

  clearDial() {
    this.dialPadValue = '';
    this.dialInput = '';
  }

  syncDialInput(value: string) {
    this.dialPadValue = value;
    this.dialInput = value;
  }

  handleEndCall()  { this.csrCall.endCall(); }
  handleWrapUp() {
    void this.csrCall.saveWrapUp(this.buildWrapUpDetails());
    this.wrapNote = '';
    this.resetWrapUpDetails();
    this.showToast('Interaction saved successfully');
  }
  handleCaptions() { void this.csrCall.toggleCaptions(); }
  handleRecording(){ this.csrCall.toggleRecording(); }

  onNotesInput() {
    if (this.notesDebounce) clearTimeout(this.notesDebounce);
    this.notesDebounce = setTimeout(() => { this.csrCall.saveDraftNote(this.wrapNote, this.wrapUpDetails); }, 500);
  }

  private buildWrapUpDetails(): WrapUpDetails {
    return {
      ...this.wrapUpDetails,
      notes: this.wrapNote,
      callbackStatus: this.wrapUpDetails.callbackRequired ? this.wrapUpDetails.callbackStatus : '',
      callbackDueAt: this.wrapUpDetails.callbackRequired ? this.wrapUpDetails.callbackDueAt : '',
    };
  }

  private resetWrapUpDetails() {
    this.wrapUpDetails = {
      disposition: '',
      callReason: '',
      sourceOfInformation: '',
      callbackRequired: false,
      callbackDueAt: '',
      callbackStatus: 'PENDING',
    };
  }

  initials(name: string): string { return name.split(' ').map(n => n[0] ?? '').join('').toUpperCase(); }
  goToHistory()    { void this.router.navigate(['/telephony/history']); }
  changeExtension(){ this.csrCall.selectExtension('', ''); }
}
